/**
 * This is intended as a debug page for triggering a render of a building in our nascent render pipeline.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ClientStudioDocMetadata } from "api-types/studioDocs/studioDocMetadataTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import colors from "common/constants/colorsOld";
import { fetcher, useFetch } from "lib/fetcher";
import { useUserContext } from "lib/state/UserContextProvider";
import getPageTitle from "lib/utils/getPageTitle";
import { useCallback, useMemo, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import Input from "ui/forms/Input";
import useAppContext from "ui/hooks/useAppContext";
import IconExit from "ui/icons/IconExit";
import { HorizontalSplitGroup, SplitAlignment } from "ui/layouts/Split";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import withAuthentication from "../../components/withAuthentication";

const statusToColor = {
  RUNNING: colors.sapphire,
  FAILED: colors.ruby,
  SUCCEEDED: colors.emerald,
  "N/A": "black",
  "...": "black",
};

function RenderTestPage() {
  const [hideUntitled, setHideUntitled] = useState(true);
  const [showMyPlansOnly, setShowMyPlansOnly] = useState(false);
  const { user } = useUserContext();
  const { data: studioDocs } = useFetch<ClientStudioDocMetadata[]>(
    `${WEB_API_BASE_PATH}/studio-docs?hideUntitled=${hideUntitled}${showMyPlansOnly ? `&userId=${user?.id}` : ""}`,
    [user?.id],
  );

  type RenderJob = { name: string; startDate: string; status: string };
  type CompletedRenderJob = { s3Url: string };
  const { data: renderJobStatus, mutate } = useFetch<{
    executions: RenderJob[];
    completedExecutions: { [name: string]: CompletedRenderJob };
  }>(`${WEB_API_BASE_PATH}/debug/list-render-jobs`, [user?.id], { refreshInterval: 5000 });

  const [focusedJob, setFocusedJob] = useState<CompletedRenderJob | undefined>();
  const [search, setSearch] = useState<string | null>(null);
  const { setUserNotification } = useAppContext();

  type StudioDocMetadataWithJobStatus = ClientStudioDocMetadata & {
    renderJobStatus: string;
    executionName: string | undefined;
  };

  const joined: StudioDocMetadataWithJobStatus[] = useMemo(() => {
    if (!studioDocs) {
      return [];
    }

    if (!renderJobStatus) {
      return studioDocs.map((x) => ({ ...x, executionName: undefined, renderJobStatus: "..." }));
    }

    return studioDocs.map((studioDoc) => {
      const jobs = renderJobStatus.executions
        .filter((job) => job.name.startsWith(`studioDocRenderJob-${studioDoc.id}`))
        .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
      return { ...studioDoc, executionName: jobs[0]?.name, renderJobStatus: jobs[0]?.status || "N/A" };
    });
  }, [renderJobStatus, studioDocs]);

  const isFiltered = useCallback(
    (doc: ClientStudioDocMetadata): boolean => {
      if (!search) {
        return true;
      }
      const docFields = [doc.name, doc.firstName, doc.lastName, doc.email].filter(Boolean);
      return docFields.filter((s) => s && s.toLowerCase().indexOf(search.toLowerCase()) > -1).length > 0;
    },
    [search],
  );

  const homes = joined?.filter((p) => isFiltered(p)) ?? [];
  const columns: Column<StudioDocMetadataWithJobStatus>[] = [
    { header: "Plan Name", field: "name", bold: true },
    { header: "Author", accessor: (p) => `${p.firstName} ${p.lastName} (${p.email})` },
    {
      header: "Status",
      width: 0.1,
      accessor: (p) => {
        if (p.renderJobStatus === "SUCCEEDED") {
          return (
            <span
              onClick={() => setFocusedJob(renderJobStatus?.completedExecutions[p.executionName ?? ""])}
              style={{ color: colors.emerald }}
            >
              {p.renderJobStatus}
            </span>
          );
        }

        return <span style={{ color: statusToColor[p.renderJobStatus] }}>{p.renderJobStatus}</span>;
      },
    },
    {
      id: "render",
      width: 0.1,
      accessor: function Render(p) {
        return (
          <button
            onClick={async () => {
              try {
                await fetcher(`${WEB_API_BASE_PATH}/debug/start-render-job/${p.id}`);
                await mutate();
                setUserNotification({ message: "Successfully started render job", messageType: "success" });
              } catch (e) {
                setUserNotification({
                  message: "Could not start render job:\n" + JSON.stringify(e),
                  messageType: "error",
                });
              }
            }}
          >
            Start Render
          </button>
        );
      },
    },
  ];

  const table = (
    <div style={{ padding: 24 }}>
      <h2>Render Jobs</h2>
      <div style={{ display: "flex" }}>
        <div style={{ flex: 1 }}>
          <Input
            style={{ width: 200, height: 36 }}
            inputSize="medium"
            placeholder="Search Homes"
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <label style={{ whiteSpace: "nowrap" }}>
          <input
            type="checkbox"
            style={{ marginRight: 6 }}
            checked={hideUntitled}
            onChange={(e) => setHideUntitled(e.currentTarget.checked)}
          />
          Hide Untitled
        </label>
        <label style={{ whiteSpace: "nowrap" }}>
          <input
            type="checkbox"
            style={{ marginRight: 6 }}
            checked={showMyPlansOnly}
            onChange={(e) => setShowMyPlansOnly(e.currentTarget.checked)}
          />
          My Homes Only
        </label>
      </div>
      <ConfigTable columns={columns} data={homes} onRowClick={(row) => console.log(row)} />
    </div>
  );
  return (
    <>
      <Head>
        <title>{getPageTitle("Render Test")}</title>
      </Head>
      {focusedJob ? (
        <HorizontalSplitGroup
          name="commands-left"
          initialFixedSideSize={800}
          flexibleSide={SplitAlignment.FIRST}
          minPanelSize={800}
        >
          {table}
          <div style={{ padding: 24 }}>
            <IconExit size={24} style={{ color: "white" }} onClick={() => setFocusedJob(undefined)} />
            <img style={{ width: "100%" }} src={focusedJob.s3Url} />
          </div>
        </HorizontalSplitGroup>
      ) : (
        table
      )}
    </>
  );
}

export const Route = createFileRoute("/debug/render")({
  component: withAuthentication(RenderTestPage, { superAdmin: true }),
});
